import React from 'react';
import { useSelector } from 'react-redux';
import selectedTerminalIdSelector from '../../redux/reducers/SelectedTerminalSlice/selectedTerminalSliceSelector';
import { ITerminalProps } from '../../redux/reducers/TerminalSlice/interfaces';
import {
  isTerminalTxFlowSuccess,
  terminalInstance,
  terminalTxFlowAwaitingSignatureTracker,
  terminalTxTotalAmount,
} from '../../redux/reducers/TerminalSlice/terminalsSliceSelectors';
import currencyFormat from '../../utils/common/intl/currencyFormatter';
import { CloudTransactionResponse } from '../../transaction-handling/cloud-transaction';

interface PurchaseFlowProps {
  cloudTransactionData?: CloudTransactionResponse | null;
}

export default function PurchaseFlow({ cloudTransactionData }: PurchaseFlowProps = {}): React.ReactElement {
  const selectedTerminal = useSelector(selectedTerminalIdSelector);
  const currentTerminal = useSelector(terminalInstance(selectedTerminal.id)) as ITerminalProps;
  const totalAmount = useSelector(terminalTxTotalAmount(selectedTerminal.id));
  const isSuccess = useSelector(isTerminalTxFlowSuccess(selectedTerminal.id));
  const awaitingSignatureCheck = useSelector(terminalTxFlowAwaitingSignatureTracker(selectedTerminal.id));

  const isCloudMode = !!cloudTransactionData;

  const cloudStatusInformation = () => {
    if (cloudTransactionData?.error) {
      return `
${JSON.stringify(cloudTransactionData, null, 2)}
`;
    }
    const data = cloudTransactionData?.data;
    if (!data) return 'No cloud transaction data';

    const amounts = data.result_amounts;
    const requestAmount = amounts ? Object.values(amounts).reduce((sum, val) => sum + val, 0) : 0;

    return `
# Transaction ID: ${data.id}
# Message: ${data.message}
# Request Amount: ${currencyFormat(requestAmount / 100)}
# Financial Status: ${data.result_financial_status}
# Status: ${data.status}
`;
  };

  const statusInformation = (spi: ITerminalProps) =>
    awaitingSignatureCheck
      ? `
# ----------- STATUS -----------

# WOOHOO - WE GOT PAID!
# POS Reference: ${spi.txFlow?.request?.data?.posRefId}
# Response: ${spi.txFlow?.response?.data?.hostResponseText}
# RRN: ${spi.txFlow?.response?.data?.rrn}
# Scheme: ${spi.txFlow?.response?.data?.schemeName}

# ----------- Customer Receipt -----------

# PURCHASE: ${currencyFormat((spi?.txFlow?.response?.data?.purchaseAmount ?? 0) / 100)}
# TIP: ${currencyFormat((spi?.txFlow?.response?.data?.tipAmount ?? 0) / 100)}
# SURCHARGE: ${currencyFormat((spi?.txFlow?.response?.data?.surchargeAmount ?? 0) / 100)}
# CASHOUT: ${currencyFormat((spi?.txFlow?.response?.data?.bankCashAmount ?? 0) / 100)}

`
      : `
### TX PROCESS UPDATE ###

# ${spi?.txFlow?.displayMessage}
# Request Id: ${spi?.txFlow?.signatureRequiredMessage?.requestId}
# PosRefId: ${spi?.txFlow?.posRefId}
# Type: ${spi?.txFlow?.type}
# Request Amount: ${currencyFormat(totalAmount / 100)}
# Waiting For Signature: ${spi?.txFlow?.awaitingSignatureCheck}
# Finished: ${spi?.txFlow?.finished}
# Success: ${spi?.txFlow?.success}
`;

  const failedStatusInformation = (spi: ITerminalProps) => `
# ----------- STATUS -----------

# WE DID NOT GET PAID :(
# Error Detail: ${spi?.txFlow?.displayMessage}
# Host Response: ${spi?.txFlow?.response?.data?.hostResponseText}
# RRN: ${spi?.txFlow?.response?.data?.rrn}
`;

  if (isCloudMode) {
    return <pre>{cloudStatusInformation()}</pre>;
  }

  return <pre>{isSuccess ? statusInformation(currentTerminal) : failedStatusInformation(currentTerminal)}</pre>;
}
